/**
 * 核心概览组件
 * 使用 RichTextBlock 展示 AI 生成的核心概览摘要
 */
import React, { useMemo } from 'react';
import RichTextBlock from '@op/components/RichTextBlock';
import useLanguage from '@op/hooks/useLanguage';
import { useCoreOverviewSummary } from '../../hooks/useCoreOverviewSummary';
import { DefaultImg } from '@op/constants/defaultImg';
import { SubmitValueType } from '@op/components/RichTextBlock/RichTextEditModal';
import './index.scss';
import moment from 'moment';

interface IOverviewCardProps {
  filter: any;
  gameInfo: any;
  wholeGameInfo: any;
}

/** 递归判断值是否为默认值（空数组、空字符串、'99999'、或含这些值的对象） */
const isDefaultValue = (val: any): boolean => {
  if (val === '' || val === '99999') return true;
  if (Array.isArray(val)) {
    if (val.length === 0) return true;
    // e.g. categories: [{ category: "99999", sub_categories: [] }]
    return val.every((item) => {
      if (typeof item === 'object' && item !== null) {
        return Object.values(item).every((v) => isDefaultValue(v));
      }
      return item === '99999' || item === '';
    });
  }
  if (typeof val === 'object' && val !== null) {
    return Object.values(val).every((v) => isDefaultValue(v));
  }
  return false;
};

const OverviewCard: React.FC<IOverviewCardProps> = ({ filter, gameInfo }) => {
  const { formatMessage } = useLanguage();

  // 判断是否跨周（开始时间和结束时间不在同一周内）
  const isCrossWeek = useMemo(() => {
    const startTime = moment(filter?.start_time);
    const endTime = moment(filter?.end_time);
    // 获取开始时间所在周的周日
    const weekEnd = moment(startTime).endOf('week');
    // 如果结束时间超过了开始时间所在周的周日，则跨周
    return endTime.isAfter(weekEnd);
  }, [filter]);

  // 构建请求参数
  const filterParams = useMemo(() => {
    if (!gameInfo?.edition_unified_id) return null;
    const { start_time, end_time } = filter || {};
    return {
      start_time,
      end_time,
      ...gameInfo,
    };
  }, [filter, gameInfo]);

  const hideSummary = useMemo(() => {
    // 需要跳过默认值检查的 key（时间字段 + gameInfo 字段）
    const SKIP_KEYS = ['start_time', 'end_time', 'date_type', 'edition_unified_id', 'entity_type', 'id_type'];

    // 检查除 SKIP_KEYS 外的 filter 值是否存在非默认值，有则不请求 summary
    const restEntries = Object.entries(filter || {}).filter(
      ([key]) => !SKIP_KEYS.includes(key),
    );
    return restEntries.some(([, val]) => !isDefaultValue(val));
  }, [filter]);

  // 使用 Hook 获取核心概览摘要数据（已转换为 RichTextBlock 格式）
  const { richText, loading } = useCoreOverviewSummary(filterParams, {
    autoFetch: true,
    hideSummary
  });

  // 自定义标题
  const titleElement = (
    <div className="dbo-overview-card__title">
      <img src={DefaultImg.AI} className="dbo-overview-card__icon" alt="AI" />
      <span className="dbo-overview-card__label">
        {formatMessage({ id: 'Executive Summary' })}
      </span>
    </div>
  );

  return (
    <RichTextBlock
      className="dbo-overview-card"
      tabs={richText}
      loading={loading}
      loadingHeight={200}
      emptyHeight={200}
      title={titleElement}
      singleModel
      canEdit={false}
      bodyStyle={{ marginBottom: '20px' }}
      hasEmptyImg={false}
      emptyProps={{
        hasImg: false,
        hasTitle: false,
        contentCard: true,
        hasDesc: false,
        height: 200,
        emptyDescElement: () =>
          formatMessage({
            id: isCrossWeek || hideSummary
              ? 'Custom date range is not supported at this time for AI summaries.'
              : 'No data for the current selection.',
          }),
      }}
      onConfirm={(_item: SubmitValueType) => Promise.resolve(true)}
    />
  );
};

export default OverviewCard;
